import { ADMIN_DEVICE_KEY } from '~/constants/analytics'

/**
 * Simple analytics composable without store dependency
 */
export const useAnalytics = () => {
  // Taken here, not when an event fires: that is often after an await in a
  // click handler, when the Nuxt context is gone.
  const route = useRoute()

  // Check if tracking is enabled
  const isTrackingEnabled = (): boolean => {
    if (!process.client) return false
    if (localStorage.getItem(ADMIN_DEVICE_KEY) === 'true') return false
    const stored = localStorage.getItem('analytics_tracking_enabled')
    return stored !== 'false' // default true, unless explicitly disabled
  }

  // Get visitor ID
  const getVisitorId = (): string | null => {
    if (!process.client) return null
    return localStorage.getItem('visitor_id')
  }

  // Get session ID
  const getSessionId = (): string | null => {
    if (!process.client) return null
    return sessionStorage.getItem('session_id')
  }

  // Toggle tracking
  const toggleTracking = (enabled: boolean) => {
    if (!process.client) return
    localStorage.setItem('analytics_tracking_enabled', enabled.toString())
  }

  // Track custom event
  const trackCustomEvent = async (eventType: string, eventData: Record<string, any>) => {
    if (!isTrackingEnabled() || !process.client) return

    const visitorId = getVisitorId()
    const sessionId = getSessionId()
    
    if (!visitorId || !sessionId) return

    const event = {
      visitor_id: visitorId,
      session_id: sessionId,
      event_type: eventType,
      event_data: eventData,
      page_path: route.path,
      timestamp: new Date().toISOString()
    }

    try {
      await $fetch('/api/analytics/event', {
        method: 'POST',
        body: event
      })
    } catch (error) {
      console.warn('Failed to track custom event:', error)
    }
  }

  // Track user action
  const trackUserAction = (action: string, details?: Record<string, any>) => {
    return trackCustomEvent('user_action', {
      action,
      ...details
    })
  }

  // Track feature usage
  const trackFeatureUsage = (feature: string, data?: Record<string, any>) => {
    return trackCustomEvent('feature_usage', {
      feature,
      ...data
    })
  }

  // Track error
  const trackError = (errorType: string, errorMessage: string, errorStack?: string) => {
    return trackCustomEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack
    })
  }

  // Fetch analytics stats
  const fetchStats = async (dateRange?: { from: string; to: string }) => {
    try {
      const query = dateRange ? `?from=${dateRange.from}&to=${dateRange.to}` : ''
      const response = await $fetch(`/api/analytics/stats${query}`)
      return response
    } catch (error) {
      console.error('Failed to fetch analytics stats:', error)
      throw error
    }
  }

  // Export analytics data
  const exportAnalyticsData = async (format: 'json' | 'csv' = 'json', dateRange?: { from: string; to: string }) => {
    try {
      const query = new URLSearchParams()
      if (dateRange) {
        query.append('from', dateRange.from)
        query.append('to', dateRange.to)
      }
      query.append('format', format)

      const response = await $fetch(`/api/analytics/export?${query.toString()}`, {
        method: 'GET'
      })

      // Create and download file
      const blob = new Blob([typeof response === 'string' ? response : JSON.stringify(response)], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      })
      
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `analytics_${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      return response
    } catch (error) {
      console.error('Failed to export analytics data:', error)
      throw error
    }
  }

  return {
    isTrackingEnabled,
    getVisitorId,
    getSessionId,
    toggleTracking,
    trackCustomEvent,
    trackUserAction,
    trackFeatureUsage,
    trackError,
    fetchStats,
    exportAnalyticsData
  }
}